import { logger } from './logger';

const supportsLocalStorage = () => {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return false;
    }

    const storage = window.localStorage;
    const testKey = '__storage_test__';

    storage.setItem(testKey, testKey);
    storage.getItem(testKey);
    storage.removeItem(testKey);

    return true; // localStorage is available
  } catch (e) {
    logger('localStorage').exception({
      error: e,
      tags: { source: 'localStorage' }
    });
    return false;
  }
};

const isLocalStorageSupported = supportsLocalStorage();

// In case of old browsers, privacy restrictions or eventual SSR, return null.
export const store = {
  getItem: <T>(
    key: string,
    defaultValue: T | null = null,
    typeChecker?: (value: unknown) => value is T
  ): T | null => {
    if (!isLocalStorageSupported) {
      return null;
    }
    try {
      const serialized = window.localStorage.getItem(key) as unknown;
      if (typeof serialized === 'string') {
        const parsedValue = JSON.parse(serialized) as unknown;
        if (parsedValue !== null) {
          if (!typeChecker) {
            return parsedValue as T;
          } else if (typeChecker(parsedValue)) {
            return parsedValue;
          }
        }
      }
    } catch (e) {
      logger('localStorage').exception({
        error: e,
        tags: { key, source: 'localStorage.getItem' }
      });
    }
    return defaultValue;
  },
  setItem: <T>(key: string, value: T | null): boolean => {
    if (!isLocalStorageSupported) {
      return false;
    }
    try {
      if (value === undefined || value === null) {
        window.localStorage.removeItem(key);
      } else {
        const serialized = JSON.stringify(value);
        window.localStorage.setItem(key, serialized);
      }
      return true;
    } catch (e) {
      logger('localStorage').exception({
        error: e,
        tags: { key, source: 'localStorage.setItem' }
      });
      return false;
    }
  }
};
